import * as signalR from '@microsoft/signalr';
import { useAuthenticationStore } from '@/stores/authenticationStore';

class SignalRService {
  private connection: signalR.HubConnection | null = null;
  private isConnecting = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private listeners: Map<string, Function[]> = new Map();

  private isUnauthorizedError(error: unknown): boolean {
    const message = error instanceof Error ? error.message : String(error);
    return message.includes('401') || message.toLowerCase().includes('unauthorized');
  }

  async connect(): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected || this.isConnecting) {
      return;
    }

    this.isConnecting = true;

    try {
      const authStore = useAuthenticationStore();
      const token = authStore.tokens?.token;

      if (!token) {
        console.warn('Cannot connect to SignalR: No authentication token');
        this.isConnecting = false;
        return;
      }

      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000';
      const hubUrl = `${apiUrl}/notificationHub`;

      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl, {
          // Must read the latest token dynamically so reconnects work after refresh.
          accessTokenFactory: () => authStore.tokens?.token ?? '',
          withCredentials: false,
          transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.ServerSentEvents,
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            if (retryContext.previousRetryCount >= this.maxReconnectAttempts) {
              return null; // Stop reconnecting
            }
            // Exponential backoff: 2s, 4s, 8s, 16s, 32s
            return Math.min(1000 * Math.pow(2, retryContext.previousRetryCount + 1), 30000);
          }
        })
        .configureLogging(signalR.LogLevel.Information)
        .build();

      // Setup event handlers
      this.connection.onreconnecting(() => {
        console.log('SignalR reconnecting...');
      });

      this.connection.onreconnected(() => {
        console.log('SignalR reconnected successfully');
        this.reconnectAttempts = 0;
      });

      this.connection.onclose((error) => {
        console.log('SignalR connection closed', error);
        this.isConnecting = false;
      });

      // Re-attach all registered listeners
      this.listeners.forEach((callbacks, method) => {
        callbacks.forEach(callback => {
          this.connection?.on(method, callback as any);
        });
      });

      await this.connection.start();
      console.log('SignalR connected successfully');
      this.isConnecting = false;
      this.reconnectAttempts = 0;
    } catch (error) {
      console.error('Failed to connect to SignalR:', error);
      this.isConnecting = false;

      // Common in production: tab idle pauses refresh timer, access token expires, negotiate returns 401.
      // Try a token refresh once, then retry the connection.
      if (this.isUnauthorizedError(error)) {
        const authStore = useAuthenticationStore();
        const refreshed = await authStore.tryRefreshToken();
        if (refreshed) {
          await this.disconnect();
          this.reconnectAttempts = 0;
          return this.connect();
        }
      }

      this.reconnectAttempts++;

      // Retry with exponential backoff
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
        console.log(`Retrying SignalR connection in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
        setTimeout(() => this.connect(), delay);
      }
    }
  }

  async disconnect(): Promise<void> {
    if (this.connection) {
      try {
        await this.connection.stop();
        console.log('SignalR disconnected');
      } catch (error) {
        console.error('Error disconnecting SignalR:', error);
      }
      this.connection = null;
      this.listeners.clear();
    }
  }

  on(method: string, callback: Function): void {
    if (!this.listeners.has(method)) {
      this.listeners.set(method, []);
    }
    this.listeners.get(method)!.push(callback);

    if (this.connection) {
      this.connection.on(method, callback as any);
    }
  }

  off(method: string, callback?: Function): void {
    if (callback) {
      const callbacks = this.listeners.get(method);
      if (callbacks) {
        const index = callbacks.indexOf(callback);
        if (index > -1) {
          callbacks.splice(index, 1);
        }
      }
      this.connection?.off(method, callback as any);
    } else {
      this.listeners.delete(method);
      this.connection?.off(method);
    }
  }

  isConnected(): boolean {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }
}

export const signalRService = new SignalRService();
